import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MethodologyPanel = ({ activeCase, onStepComplete, onQualityCheck }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [qualityChecks, setQualityChecks] = useState({});

  // European Code of Standards methodology steps
  const methodologySteps = [
    {
      id: 'source_verification',
      title: 'Source Verification',
      description: 'Verify the credibility and authenticity of primary sources',
      requirements: [
        'Identify original source of the claim',
        'Assess source credibility and track record',
        'Check for potential conflicts of interest',
        'Verify publication date and context'
      ],
      qualityChecks: [
        'Primary source identified and documented',
        'Source credibility assessment completed',
        'Potential biases or conflicts noted',
        'Publication context verified'
      ],
      estimatedTime: '15-30 minutes',
      priority: 'critical'
    },
    {
      id: 'evidence_collection',
      title: 'Evidence Collection',
      description: 'Gather comprehensive evidence from multiple reliable sources',
      requirements: [
        'Collect supporting and contradicting evidence',
        'Consult expert opinions and official statements',
        'Review relevant scientific studies or reports',
        'Document all evidence with proper attribution'
      ],
      qualityChecks: [
        'Multiple evidence sources documented',
        'Expert consultations recorded',
        'Scientific/official sources included',
        'All evidence properly attributed'
      ],
      estimatedTime: '30-60 minutes',
      priority: 'critical'
    },
    {
      id: 'forensic_analysis',
      title: 'Forensic Analysis',
      description: 'Conduct technical analysis of digital content and metadata',
      requirements: [
        'Perform reverse image/video searches',
        'Analyze metadata and technical properties',
        'Check for signs of manipulation or editing',
        'Verify timestamps and geolocation data'
      ],
      qualityChecks: [
        'Reverse search completed',
        'Metadata analysis documented',
        'Manipulation assessment performed',
        'Technical verification recorded'
      ],
      estimatedTime: '20-45 minutes',
      priority: 'high'
    },
    {
      id: 'cross_verification',
      title: 'Cross-Verification',
      description: 'Verify findings across multiple independent sources',
      requirements: [
        'Cross-check facts with authoritative sources',
        'Verify statistical claims and data',
        'Consult multiple expert opinions',
        'Check consistency across different reports'
      ],
      qualityChecks: [
        'Facts cross-checked with authorities',
        'Statistical data verified',
        'Multiple expert opinions gathered',
        'Consistency assessment completed'
      ],
      estimatedTime: '25-40 minutes',
      priority: 'high'
    },
    {
      id: 'context_assessment',
      title: 'Context Assessment',
      description: 'Evaluate the broader context and potential impact',
      requirements: [
        'Assess historical and cultural context',
        'Evaluate potential harm or misinformation impact',
        'Consider timing and political implications',
        'Review similar claims and patterns'
      ],
      qualityChecks: [
        'Historical context documented',
        'Impact assessment completed',
        'Timing considerations noted',
        'Pattern analysis performed'
      ],
      estimatedTime: '20-35 minutes',
      priority: 'medium'
    },
    {
      id: 'verdict_formulation',
      title: 'Verdict Formulation',
      description: 'Formulate clear, evidence-based verdict with confidence scoring',
      requirements: [
        'Synthesize all evidence into clear conclusion',
        'Assign appropriate confidence level',
        'Provide clear reasoning for verdict',
        'Identify any limitations or uncertainties'
      ],
      qualityChecks: [
        'Evidence synthesis completed',
        'Confidence level assigned',
        'Reasoning clearly documented',
        'Limitations acknowledged'
      ],
      estimatedTime: '15-25 minutes',
      priority: 'critical'
    }
  ];

  const handleStepComplete = (stepId) => {
    if (!completedSteps?.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
      onStepComplete?.(stepId);
    }
  };

  const handleQualityCheckToggle = (stepId, checkIndex) => {
    const key = `${stepId}_${checkIndex}`;
    setQualityChecks(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const getStepStatus = (stepId) => {
    if (completedSteps?.includes(stepId)) return 'completed';
    if (stepId === methodologySteps?.[currentStep]?.id) return 'active';
    return 'pending';
  };

  const getStepProgress = (stepId) => {
    const step = methodologySteps?.find(s => s?.id === stepId);
    if (!step) return 0;
    
    const completedChecks = step?.qualityChecks?.filter((_, index) => 
      qualityChecks?.[`${stepId}_${index}`]
    )?.length;
    
    return (completedChecks / step?.qualityChecks?.length) * 100;
  };

  const overallProgress = (completedSteps?.length / methodologySteps?.length) * 100;

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="CheckSquare" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Methodology Guide</h3>
            <p className="text-sm text-muted-foreground">
              European Code of Standards • {Math.round(overallProgress)}% complete
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <div className="w-16 h-2 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <span className="text-sm font-medium text-foreground">
            {completedSteps?.length}/{methodologySteps?.length}
          </span>
        </div>
      </div>
      {/* Steps List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {methodologySteps?.map((step, index) => {
            const status = getStepStatus(step?.id);
            const progress = getStepProgress(step?.id);
            
            return (
              <div
                key={step?.id}
                className={`
                  border rounded-lg transition-smooth
                  ${status === 'active' ? 'border-primary bg-primary/5' :
                    status === 'completed'? 'border-success bg-success/5' : 'border-border bg-card'
                  }
                `}
              >
                {/* Step Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setCurrentStep(index)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                        ${status === 'completed' ? 'bg-success text-success-foreground' :
                          status === 'active' ? 'bg-primary text-primary-foreground' :
                          'bg-muted text-muted-foreground'
                        }
                      `}>
                        {status === 'completed' ? (
                          <Icon name="Check" size={16} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-foreground">{step?.title}</h4>
                        <p className="text-sm text-muted-foreground">{step?.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${step?.priority === 'critical' ? 'bg-destructive/10 text-destructive' :
                          step?.priority === 'high'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                        }
                      `}>
                        {step?.priority}
                      </div>
                      
                      <Icon
                        name={currentStep === index ? "ChevronUp" : "ChevronDown"}
                        size={16}
                        className="text-muted-foreground"
                      />
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1 bg-border rounded-full overflow-hidden">
                    <div
                      className={`
                        h-full transition-all duration-300
                        ${status === 'completed' ? 'bg-success' :
                          status === 'active'? 'bg-primary' : 'bg-muted'
                        }
                      `}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                {/* Step Details */}
                {currentStep === index && (
                  <div className="border-t border-border p-4 space-y-4">
                    {/* Requirements */}
                    <div>
                      <h5 className="font-medium text-foreground mb-2">Requirements</h5>
                      <ul className="space-y-2">
                        {step?.requirements?.map((requirement, reqIndex) => (
                          <li key={reqIndex} className="flex items-start space-x-2 text-sm">
                            <Icon name="Circle" size={12} className="text-muted-foreground mt-1 flex-shrink-0" />
                            <span className="text-muted-foreground">{requirement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Quality Checks */}
                    <div>
                      <h5 className="font-medium text-foreground mb-2">Quality Checks</h5>
                      <div className="space-y-2">
                        {step?.qualityChecks?.map((check, checkIndex) => {
                          const isChecked = qualityChecks?.[`${step?.id}_${checkIndex}`];
                          return (
                            <label
                              key={checkIndex}
                              className="flex items-start space-x-2 cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked || false}
                                onChange={() => handleQualityCheckToggle(step?.id, checkIndex)}
                                className="mt-1 rounded border-border text-primary focus:ring-primary"
                              />
                              <span className={isChecked ? 'text-foreground' : 'text-muted-foreground'}>
                                {check}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Step Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <div className="text-sm text-muted-foreground">
                        Estimated time: {step?.estimatedTime}
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="HelpCircle"
                          onClick={() => {
                            // Show methodology help
                          }}
                        >
                          Help
                        </Button>
                        
                        <Button
                          variant={status === 'completed' ? 'success' : 'default'}
                          size="sm"
                          iconName={status === 'completed' ? 'Check' : 'CheckSquare'}
                          onClick={() => handleStepComplete(step?.id)}
                          disabled={progress < 100}
                        >
                          {status === 'completed' ? 'Completed' : 'Mark Complete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Footer */}
      <div className="border-t border-border p-4 bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Following European Code of Standards for Fact-Checking
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              onClick={() => {
                // Export methodology report
              }}
            >
              Export Report
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Share"
              onClick={() => {
                // Share methodology
              }}
            >
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodologyPanel;